// src/Analytics.js

import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList
} from 'recharts';
import { Link } from 'react-router-dom';
import NavBar from './NavBar';
import wiproLogo from './assets/wipro.png';
import { FaTicketAlt, FaClock, FaPause, FaFileAlt, FaUsers, FaChartLine } from 'react-icons/fa';
import './AnalyticsPage.css';
import './AgentAssist.css';

const Analytics = ({ userName, userEmail, onLogout, canViewUserAnalytics = false }) => {
  const COLORS = ['#7B3FF2', '#3b82f6', '#f59e0b', '#10b981', '#ef4444'];

  const fetchData = async () => {
    const API_URL = "/api/tickets"
    const response = await fetch(API_URL, { method: "GET" })
    return response
  }
 
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load cached data on mount or fetch if no cache
  useEffect(() => {
    console.log('Analytics: Loading...');
    
    // Try to load from cache first
    const cachedData = localStorage.getItem('agentAssistTicketsCache');
    if (cachedData) {
      try {
        const cache = JSON.parse(cachedData);
        // Handle both old format (array) and new format (object with tickets and timestamp)
        const tickets = cache.tickets || cache;
        setData(tickets);
        console.log('Analytics: Loaded from cache:', tickets.length, 'tickets');
        return; // Use cache, don't fetch
      } catch (error) {
        console.error('Analytics: Error loading cache:', error);
      }
    }
    
    // No cache, fetch data
    handleSyncData();
  }, []);

  // Function to fetch fresh data
  const handleSyncData = async () => {
    setLoading(true);
    console.log('Analytics: Fetching fresh data...');
    
    try {
      const response = await fetchData();
      console.log('Analytics: Response received:', response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      console.log('Analytics: Raw API result:', result);
      
      if (result.result && result.result.length > 0) {
        const tickets = result.result.map(item => ({
          ticketId: item.number,
          shortDescription: item.short_description,
          assignmentGroup: item.assignment_group,
          receivedOn: item.opened_at,
          assignedTo: item.assigned_to || '',
          status: item.incident_state || item.state,
          item: item.cmdb_ci,
          priority: item.urgency,
          action: 'Review'
        }));
        console.log('Analytics: Processed tickets:', tickets.length);
        setData(tickets);
        
        // Cache the data with timestamp (matching AgentAssist format)
        const cacheData = {
          tickets: tickets,
          timestamp: Date.now()
        };
        localStorage.setItem('agentAssistTicketsCache', JSON.stringify(cacheData));
      }
    } catch (error) {
      console.error('Analytics: Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading analytics...</p>
    </div>
  );

  // Summary counts - matching AgentAssist page logic
  const totalTickets = data.length;
  const newTickets = data.filter(ticket => 
    ticket.status === 'New' || ticket.status === '1'
  ).length;
  const inProgressTickets = data.filter(ticket => 
    ticket.status === 'In Progress' || ticket.status === '2'
  ).length;
  const onHoldTickets = data.filter(ticket => 
    ticket.status === 'On Hold' || ticket.status === '3'
  ).length;

  // Priority breakdown
  const highPriorityTickets = data.filter(ticket => 
    ticket.priority === '1' || ticket.priority === '1 - Critical'
  ).length;
  
  const mediumPriorityTickets = data.filter(ticket => 
    ticket.priority === '2' || ticket.priority === '2 - High'
  ).length;
  
  const lowPriorityTickets = data.filter(ticket => 
    ticket.priority === '3' || ticket.priority === '3 - Low'
  ).length;
 
  // Chart: Assignment Group Ticket Count
  const assignmentGroupData = data.reduce((acc, ticket) => {
    const group = ticket.assignmentGroup || 'Unassigned';
    acc[group] = (acc[group] || 0) + 1;
    return acc;
  }, {});
 
  const assignmentGroupChartData = Object.entries(assignmentGroupData)
    .map(([group, count]) => ({ name: group, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Assigned To breakdown
  const assignedToData = data.reduce((acc, ticket) => {
    const assignee = ticket.assignedTo || 'Unassigned';
    acc[assignee] = (acc[assignee] || 0) + 1;
    return acc;
  }, {});
 
  const assignedToChartData = Object.entries(assignedToData)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
 
  // Pictorial chart: Ticket status breakdown (pie chart)
  const statusChartData = [
    { name: 'New', value: newTickets },
    { name: 'In Progress', value: inProgressTickets },
    { name: 'On Hold', value: onHoldTickets }
  ].filter(item => item.value > 0);

  // Priority breakdown for pie chart
  const priorityChartData = [
    { name: 'High Priority', value: highPriorityTickets },
    { name: 'Medium Priority', value: mediumPriorityTickets },
    { name: 'Low Priority', value: lowPriorityTickets }
  ].filter(item => item.value > 0);
 
  return (
    <div className="analytics-wrapper">
      <NavBar userName={userName} userEmail={userEmail} onLogout={onLogout} canViewUserAnalytics={canViewUserAnalytics} />
      
      <div className="analytics-content">
        {/* Header */}
        <div className="analytics-header">
          <div>
            <h1 className="analytics-title">Analytics Dashboard</h1>
            <p className="analytics-subtitle">Visualize ticket metrics and performance</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={handleSyncData} className="sync-data-btn" disabled={loading} style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? '⏳ Syncing...' : '🔄 Sync Data'}
            </button>
            <Link to="/AgentAssist" className="analytics-link">
               Home
            </Link>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card stat-total">
            <div className="stat-icon">
              <FaTicketAlt size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalTickets}</div>
              <div className="stat-label">Total Tickets</div>
            </div>
          </div>

          <div className="stat-card stat-open">
            <div className="stat-icon">
              <FaFileAlt size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{newTickets}</div>
              <div className="stat-label">New</div>
            </div>
          </div>

          <div className="stat-card stat-progress">
            <div className="stat-icon">
              <FaClock size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{inProgressTickets}</div>
              <div className="stat-label">In Progress</div>
            </div>
          </div>

          <div className="stat-card stat-hold">
            <div className="stat-icon">
              <FaPause size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{onHoldTickets}</div>
              <div className="stat-label">On Hold</div>
            </div>
          </div>

          <div className="stat-card stat-teams">
            <div className="stat-icon">
              <FaUsers size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{Object.keys(assignmentGroupData).length}</div>
              <div className="stat-label">Teams</div>
            </div>
          </div>

          <div className="stat-card stat-high">
            <div className="stat-icon">
              <FaChartLine size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{highPriorityTickets}</div>
              <div className="stat-label">High Priority</div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="charts-grid">
 
          {/* Chart 1: Assignment Group */}
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Tickets per Assignment Group</h3>
              <p className="chart-subtitle">Top 10 Tickets per assignment group</p>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={assignmentGroupChartData} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#4a5568" style={{ fontSize: '10px' }} />
                <YAxis dataKey="name" type="category" stroke="#4a5568" style={{ fontSize: '8px' }} width={140} interval={0} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }} 
                />
                <Bar dataKey="count" fill="url(#colorGradient)" radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="count" position="right" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} />
                </Bar>
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#7B3FF2" />
                    <stop offset="100%" stopColor="#5A2FC2" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 2: Top Assignees */}
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Top 10 Assignees</h3>
              <p className="chart-subtitle">Ticket distribution by agent</p>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={assignedToChartData} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#4a5568" style={{ fontSize: '10px' }} />
                <YAxis dataKey="name" type="category" stroke="#4a5568" style={{ fontSize: '9px' }} width={130} interval={0} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }} 
                />
                <Bar dataKey="count" fill="url(#colorGradient2)" radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="count" position="right" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} />
                </Bar>
                <defs>
                  <linearGradient id="colorGradient2" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#C8102E" />
                    <stop offset="100%" stopColor="#A00D25" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 3: Status Breakdown */}
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Ticket Status Breakdown</h3>
              <p className="chart-subtitle">Current status distribution</p>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  labelStyle={{ fontSize: '10px', fontWeight: '600' }}
                >
                  {statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }} 
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 4: Priority Breakdown */}
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Priority Distribution</h3>
              <p className="chart-subtitle">Tickets by priority level</p>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={priorityChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  labelStyle={{ fontSize: '10px', fontWeight: '600' }}
                >
                  {priorityChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }} 
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="login-bottom-bar">
        <div className="bottom-bar-content">
          <img src={wiproLogo} alt="Wipro" className="bottom-bar-wipro-logo" />
          <p className="bottom-bar-copyright">Copyright © 2026 Wipro LTD. All rights reserved.</p>
        </div>
      </div>
    </div>

  );

};
 
export default Analytics;